/**
 * Configuración de perfiles y modos del gestor (2026-10-06).
 * Panel lateral: lista de modos a la izquierda (el orden decide cuál gana si
 * dos horarios se pisan) y el modo elegido a la derecha.
 */
import "./gestor.css";
import "./temas.css";
import { rec_React as React } from "../../runtime/index.jsx";
import { COLORES, DIAS, horaCorta, modoNuevo } from "./api-modos.js";
import { TEMAS } from "./temas.js";

// runtime/index.jsx importa este archivo antes de definir React (import
// circular): los hooks se leen al usarlos, no al cargar el módulo.
const useEffect = (...a) => React.useEffect(...a);
const useMemo = (...a) => React.useMemo(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);

const MUESTRA = [
  [`todos`, `Todos los chats`, `No esconde ningún chat individual.`],
  [`etiquetas`, `Sólo chats con estas etiquetas`, `Más los VIP.`],
  [`solo_vip`, `Sólo VIP`, `Todo lo demás queda oculto.`],
];
const GRUPOS = [
  [`todos`, `Igual que los chats`, `Siguen la misma regla de arriba.`],
  [`etiquetados`, `Sólo grupos etiquetados`, `Con alguna de las etiquetas del modo.`],
  [`ninguno`, `Ningún grupo`, `Salvo los marcados VIP.`],
];
const PRESENCIA = [
  [`normal`, `En línea mientras uses el gestor`, `El celular no suena mientras lo usas.`],
  [`invisible`, `Invisible`, `Nunca apareces en línea, aunque abras el gestor.`],
];

function Opciones({ nombre, valor, opciones, onCambiar }) {
  return (
    <div className="gx-opciones" role="radiogroup">
      {opciones.map(([v, titulo, ayuda]) => (
        <label key={v} className="gx-opcion">
          <input type="radio" name={nombre} checked={valor === v} onChange={() => onCambiar(v)} />
          <span>{titulo}<small>{ayuda}</small></span>
        </label>
      ))}
    </div>
  );
}

function EditorEtiquetas({ elegidas, conocidas, onCambiar }) {
  const [nueva, setNueva] = useState(``);
  const todas = [...new Set([...conocidas, ...elegidas])].sort((a, b) => a.localeCompare(b, `es`));
  const alternar = (e) => onCambiar(elegidas.includes(e) ? elegidas.filter((x) => x !== e) : [...elegidas, e]);
  const agregar = () => {
    const t = nueva.trim();
    if (t && !elegidas.some((x) => x.toLowerCase() === t.toLowerCase())) onCambiar([...elegidas, t]);
    setNueva(``);
  };
  return (
    <div className="gx-fila">
      {todas.map((e) => (
        <button key={e} type="button" className={`gx-chip sin-punto${elegidas.includes(e) ? ` activo gx-c-azul` : ``}`}
          aria-pressed={elegidas.includes(e)} onClick={() => alternar(e)}>{e}</button>
      ))}
      <input className="gx-entrada" style={{ width: 150, padding: `6px 8px`, fontSize: 13 }} maxLength={30}
        placeholder="Otra etiqueta" aria-label="Otra etiqueta" value={nueva}
        onChange={(e) => setNueva(e.target.value)}
        onKeyDown={(e) => { if (e.key === `Enter`) { e.preventDefault(); agregar(); } }} />
      <button type="button" className="gx-boton chico" onClick={agregar}>Añadir</button>
    </div>
  );
}

function Horario({ h, onCambiar, onQuitar }) {
  const alternarDia = (d) => {
    const dias = h.dias.includes(d) ? h.dias.filter((x) => x !== d) : [...h.dias, d].sort();
    onCambiar({ ...h, dias });
  };
  const cruza = h.desde && h.hasta && h.hasta <= h.desde;
  return (
    <div className="gx-horario">
      <div className="gx-dias">
        {DIAS.map(([d, letra, nombre]) => (
          <button key={d} type="button" className={`gx-dia${h.dias.includes(d) ? ` on` : ``}`}
            aria-pressed={h.dias.includes(d)} aria-label={nombre} title={nombre} onClick={() => alternarDia(d)}>
            {letra}
          </button>
        ))}
      </div>
      <label className="gx-fila gx-texto">de
        <input type="time" className="gx-entrada corta" value={h.desde} onChange={(e) => onCambiar({ ...h, desde: e.target.value })} />
      </label>
      <label className="gx-fila gx-texto">a
        <input type="time" className="gx-entrada corta" value={h.hasta} onChange={(e) => onCambiar({ ...h, hasta: e.target.value })} />
      </label>
      {cruza && <span className="gx-texto">termina al día siguiente</span>}
      <span className="gx-espacio" />
      <button type="button" className="gx-boton chico peligro" onClick={onQuitar}>Quitar</button>
    </div>
  );
}

function Formulario({ inicial, conocidas, api, onGuardado, onBorrado }) {
  const [m, setM] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(``);
  const [hecho, setHecho] = useState(``);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const nombreRef = useRef(null);
  // Se monta de nuevo al cambiar de modo (key en el padre): no hace falta resetear.
  useEffect(() => { if (!inicial.id) nombreRef.current?.focus(); }, []);

  const poner = (cambios) => { setM((x) => ({ ...x, ...cambios })); setHecho(``); };
  const previa = useMemo(
    () => (m.respuesta || ``).replaceAll(`{hasta}`, `18:00`).replaceAll(`{modo}`, m.nombre || `este modo`),
    [m.respuesta, m.nombre],
  );

  const guardar = async (ev) => {
    ev.preventDefault();
    setGuardando(true); setError(``);
    try {
      const datos = { ...m, horarios: m.horarios.map(({ dias, desde, hasta }) => ({ dias, desde, hasta })) };
      const r = m.id ? await api.guardar(datos) : await api.crear(datos);
      setM(r);
      setHecho(`Guardado`);
      onGuardado(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    setGuardando(true); setError(``);
    try {
      await api.borrar(m.id);
      onBorrado();
    } catch (e) {
      setError(e.message);
      setGuardando(false);
    }
  };

  return (
    <form className="gx-form" onSubmit={guardar}>
      <section className="gx-seccion">
        <h3>Perfil</h3>
        <label className="gx-campo">Nombre
          <input ref={nombreRef} className="gx-entrada" maxLength={40} required value={m.nombre}
            onChange={(e) => poner({ nombre: e.target.value })} placeholder="Ej.: Trabajo, Gimnasio, Viaje" />
        </label>
        <div className="gx-campo">Color
          <div className="gx-colores" role="radiogroup" aria-label="Color">
            {COLORES.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={m.color === c} aria-label={c}
                className={`gx-color gx-c-${c}${m.color === c ? ` elegido` : ``}`} onClick={() => poner({ color: c })} />
            ))}
          </div>
        </div>
      </section>

      <section className="gx-seccion">
        <h3>Tema</h3>
        <label className="gx-campo">Mientras este modo esté activo
          <select className="gx-select" value={m.tema ?? ``} onChange={(e) => poner({ tema: e.target.value || null })}>
            <option value="">El tema general</option>
            {TEMAS.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </label>
      </section>

      <section className="gx-seccion">
        <h3>Qué ves</h3>
        <Opciones nombre="muestra" valor={m.muestra} opciones={MUESTRA} onCambiar={(v) => poner({ muestra: v })} />
        {(m.muestra === `etiquetas` || m.grupos === `etiquetados`) && (
          <div className="gx-campo">Etiquetas del modo
            <EditorEtiquetas elegidas={m.etiquetas} conocidas={conocidas} onCambiar={(etiquetas) => poner({ etiquetas })} />
            <small>Las etiquetas se ponen a cada chat desde el botón ··· de la lista.</small>
          </div>
        )}
        <div className="gx-campo">Grupos
          <Opciones nombre="grupos" valor={m.grupos} opciones={GRUPOS} onCambiar={(v) => poner({ grupos: v })} />
        </div>
        <small className="gx-texto">Los chats VIP se ven siempre, en cualquier modo.</small>
      </section>

      <section className="gx-seccion">
        <h3>Cómo apareces</h3>
        <Opciones nombre="presencia" valor={m.presencia} opciones={PRESENCIA} onCambiar={(v) => poner({ presencia: v })} />
      </section>

      <section className="gx-seccion">
        <h3>Cuándo se activa solo</h3>
        {m.horarios.length === 0 && <p className="gx-texto">Sin horario: sólo se activa a mano, desde la barra de modos.</p>}
        {m.horarios.map((h, i) => (
          <Horario key={i} h={h}
            onCambiar={(nuevo) => poner({ horarios: m.horarios.map((x, j) => (j === i ? nuevo : x)) })}
            onQuitar={() => poner({ horarios: m.horarios.filter((_, j) => j !== i) })} />
        ))}
        {m.horarios.length < 10 && (
          <div>
            <button type="button" className="gx-boton chico"
              onClick={() => poner({ horarios: [...m.horarios, { dias: [1, 2, 3, 4, 5], desde: `08:00`, hasta: `18:00` }] })}>
              Añadir horario
            </button>
          </div>
        )}
        <small className="gx-texto">Hora de Lima. Si dos modos coinciden, gana el que está más arriba en la lista.</small>
      </section>

      <section className="gx-seccion">
        <h3>Respuesta automática</h3>
        <label className="gx-opcion">
          <input type="checkbox" checked={m.respuestaActiva} onChange={(e) => poner({ respuestaActiva: e.target.checked })} />
          <span>Contestar sola mientras este modo esté activo
            <small>Nunca a grupos, a VIP ni a mensajes que llegaron hace más de 2 minutos.</small></span>
        </label>
        {m.respuestaActiva && (
          <>
            <label className="gx-campo">Mensaje
              <textarea className="gx-area" maxLength={500} value={m.respuesta ?? ``}
                onChange={(e) => poner({ respuesta: e.target.value })}
                placeholder="Estoy en reunión, te escribo a las {hasta}." />
              <small>{`{hasta}`} = hora en que termina el modo («más tarde» si no tiene fin). {`{modo}`} = su nombre. {(m.respuesta ?? ``).length}/500</small>
            </label>
            {previa.trim() && <div className="gx-previa" aria-label="Vista previa">{previa}</div>}
            <div className="gx-fila">
              <label className="gx-campo">Al mismo chat, cada
                <span className="gx-fila"><input type="number" min={1} max={72} className="gx-entrada corta" value={m.respuestaCadaHoras}
                  onChange={(e) => poner({ respuestaCadaHoras: Number(e.target.value) })} /> horas como mucho</span>
              </label>
              <label className="gx-campo">Tope por día
                <span className="gx-fila"><input type="number" min={1} max={200} className="gx-entrada corta" value={m.respuestaMaxDia}
                  onChange={(e) => poner({ respuestaMaxDia: Number(e.target.value) })} /> respuestas</span>
              </label>
            </div>
            <label className="gx-opcion">
              <input type="checkbox" checked={m.respuestaSoloConocidos} onChange={(e) => poner({ respuestaSoloConocidos: e.target.checked })} />
              <span>Sólo a quienes ya les escribí alguna vez<small>No contesta a números desconocidos.</small></span>
            </label>
            <label className="gx-opcion">
              <input type="checkbox" checked={m.respuestaSoloSiAusente} onChange={(e) => poner({ respuestaSoloSiAusente: e.target.checked })} />
              <span>Sólo si no estoy usando el gestor<small>Si lo tengo abierto, contesto yo.</small></span>
            </label>
          </>
        )}
      </section>

      <div className="gx-pie">
        <button type="submit" className="gx-boton primario" disabled={guardando}>{m.id ? `Guardar cambios` : `Crear modo`}</button>
        {hecho && <span className="gx-texto" role="status">{hecho}</span>}
        {error && <span className="gx-error" role="alert">{error}</span>}
        <span className="gx-espacio" />
        {m.id && !confirmarBorrado && (
          <button type="button" className="gx-boton peligro" onClick={() => setConfirmarBorrado(true)}>Borrar modo</button>
        )}
        {m.id && confirmarBorrado && (
          <span className="gx-fila">
            <span className="gx-texto">¿Borrar «{inicial.nombre}»?</span>
            <button type="button" className="gx-boton peligro" disabled={guardando} onClick={borrar}>Sí, borrar</button>
            <button type="button" className="gx-boton" onClick={() => setConfirmarBorrado(false)}>No</button>
          </span>
        )}
      </div>
    </form>
  );
}

/** Respuestas rápidas: «/atajo» en la caja de mensaje pone el texto. */
function Plantillas({ api, onCambio }) {
  const [lista, setLista] = useState(null);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState(``);
  const cargar = () => api.plantillas().then(setLista).catch((e) => setError(e.message));
  useEffect(() => { cargar(); }, [api]);
  const guardar = async (ev) => {
    ev.preventDefault();
    setError(``);
    try {
      if (editando.id) await api.guardarPlantilla(editando); else await api.crearPlantilla(editando);
      setEditando(null);
      await cargar();
      onCambio();
    } catch (e) { setError(e.message); }
  };
  const borrar = async (p) => {
    try { await api.borrarPlantilla(p.id); await cargar(); onCambio(); } catch (e) { setError(e.message); }
  };
  return (
    <div className="gx-form">
      <section className="gx-seccion">
        <h3>Respuestas rápidas</h3>
        <p className="gx-texto">En la caja de mensaje escribe <b>/</b> y el atajo: el texto se pone solo.</p>
        {!lista && !error && <p className="gx-texto">Cargando…</p>}
        {lista?.length === 0 && !editando && <p className="gx-texto">Todavía no tienes ninguna.</p>}
        {lista?.length > 0 && (
          <table className="gx-tabla">
            <tbody>
              {lista.map((p) => (
                <tr key={p.id}>
                  <td style={{ whiteSpace: `nowrap` }}>/{p.atajo}</td>
                  <td style={{ textAlign: `left`, color: `var(--text)` }}>{p.texto.length > 70 ? `${p.texto.slice(0, 70)}…` : p.texto}</td>
                  <td style={{ whiteSpace: `nowrap` }}>
                    <button type="button" className="gx-boton chico" onClick={() => setEditando({ ...p })}>Editar</button>{` `}
                    <button type="button" className="gx-boton chico peligro" onClick={() => borrar(p)}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {editando ? (
          <form className="gx-seccion" onSubmit={guardar}>
            <label className="gx-campo">Atajo
              <input className="gx-entrada" autoFocus maxLength={30} placeholder="ruta" value={editando.atajo}
                onChange={(e) => setEditando({ ...editando, atajo: e.target.value })} />
              <small>Letras, números y guiones. Se usa como /{(editando.atajo || `atajo`).toLowerCase().replace(/\s+/g, `-`)}</small>
            </label>
            <label className="gx-campo">Texto
              <textarea className="gx-area" maxLength={2000} value={editando.texto} onChange={(e) => setEditando({ ...editando, texto: e.target.value })} />
            </label>
            <div className="gx-fila">
              <button type="submit" className="gx-boton primario" disabled={!editando.atajo.trim() || !editando.texto.trim()}>Guardar</button>
              <button type="button" className="gx-boton" onClick={() => setEditando(null)}>Cancelar</button>
            </div>
          </form>
        ) : (
          <div><button type="button" className="gx-boton chico" onClick={() => setEditando({ id: null, atajo: ``, texto: `` })}>+ Nueva respuesta rápida</button></div>
        )}
        {error && <p className="gx-error" role="alert">{error}</p>}
      </section>
    </div>
  );
}

function Registro({ api }) {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(``);
  useEffect(() => { api.respuestasAuto().then(setFilas).catch((e) => setError(e.message)); }, [api]);
  return (
    <div className="gx-form">
      <section className="gx-seccion">
        <h3>Respuestas automáticas enviadas</h3>
        {error && <p className="gx-error">{error}</p>}
        {!filas && !error && <p className="gx-texto">Cargando…</p>}
        {filas?.length === 0 && <p className="gx-texto">Todavía no se contestó nada solo.</p>}
        {filas?.length > 0 && (
          <table className="gx-tabla">
            <tbody>
              {filas.map((f, i) => (
                <tr key={i}>
                  <td>{f.chat}</td>
                  <td>{f.modo ?? `modo borrado`}</td>
                  <td>{new Date(f.enviadoEn).toLocaleDateString(`es-PE`, { day: `numeric`, month: `short` })} {horaCorta(f.enviadoEn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export function ModosGestor({ api, datos, tema, onCerrar, onCambio }) {
  const [modos, setModos] = useState(datos?.modos ?? []);
  const [elegido, setElegido] = useState(datos?.modos?.[0]?.id ?? `nuevo`);
  const [nuevo, setNuevo] = useState(modoNuevo);
  const [errorOrden, setErrorOrden] = useState(``);
  const cerrarRef = useRef(null);

  useEffect(() => { if (datos?.modos) setModos(datos.modos); }, [datos]);
  useEffect(() => {
    cerrarRef.current?.focus();
    const tecla = (e) => { if (e.key === `Escape`) onCerrar(); };
    window.addEventListener(`keydown`, tecla);
    return () => window.removeEventListener(`keydown`, tecla);
  }, [onCerrar]);

  const actual = elegido === `nuevo` ? nuevo : modos.find((m) => m.id === elegido);

  const mover = async (i, paso) => {
    const lista = [...modos];
    const j = i + paso;
    if (j < 0 || j >= lista.length) return;
    [lista[i], lista[j]] = [lista[j], lista[i]];
    setModos(lista);
    try {
      await api.ordenar(lista.map((m) => m.id));
      setErrorOrden(``);
      onCambio();
    } catch (e) {
      setErrorOrden(e.message);
    }
  };

  return (
    <div className="gx gx-velo" data-tema={tema} onPointerDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
      <div className="gx-panel" role="dialog" aria-modal="true" aria-label="Configurar modos">
        <div className="gx-panel-cab">
          <h2>Perfiles y modos</h2>
          <span className="gx-espacio" />
          <button ref={cerrarRef} type="button" className="gx-boton" onClick={onCerrar}>Cerrar</button>
        </div>
        <div className="gx-panel-cuerpo">
          <nav className="gx-panel-lista" aria-label="Modos">
            {modos.map((m, i) => (
              <div key={m.id} className="gx-modo-fila">
                <button type="button" className={`gx-chip gx-c-${m.color}${elegido === m.id ? ` activo` : ``}`}
                  aria-current={elegido === m.id} onClick={() => setElegido(m.id)}>{m.nombre}</button>
                <button type="button" className="gx-icono" aria-label={`Subir ${m.nombre}`} disabled={i === 0} onClick={() => mover(i, -1)}>↑</button>
                <button type="button" className="gx-icono" aria-label={`Bajar ${m.nombre}`} disabled={i === modos.length - 1} onClick={() => mover(i, 1)}>↓</button>
              </div>
            ))}
            <button type="button" className={`gx-chip sin-punto${elegido === `nuevo` ? ` activo gx-c-azul` : ``}`}
              onClick={() => { setNuevo(modoNuevo()); setElegido(`nuevo`); }}>+ Nuevo modo</button>
            <button type="button" className={`gx-chip sin-punto${elegido === `plantillas` ? ` activo gx-c-gris` : ``}`}
              onClick={() => setElegido(`plantillas`)}>Respuestas rápidas</button>
            <button type="button" className={`gx-chip sin-punto${elegido === `registro` ? ` activo gx-c-gris` : ``}`}
              onClick={() => setElegido(`registro`)}>Respuestas automáticas enviadas</button>
            {errorOrden && <p className="gx-error">{errorOrden}</p>}
          </nav>
          {elegido === `registro` ? <Registro api={api} /> : elegido === `plantillas` ? <Plantillas api={api} onCambio={onCambio} /> : actual ? (
            <Formulario
              key={actual.id ?? `nuevo`}
              inicial={actual}
              conocidas={datos?.etiquetas ?? []}
              api={api}
              onGuardado={(r) => {
                setModos((ms) => (ms.some((x) => x.id === r.id) ? ms.map((x) => (x.id === r.id ? r : x)) : [...ms, r]));
                setElegido(r.id);
                onCambio();
              }}
              onBorrado={() => {
                const resto = modos.filter((x) => x.id !== actual.id);
                setModos(resto);
                setElegido(resto[0]?.id ?? `nuevo`);
                onCambio();
              }}
            />
          ) : <div className="gx-form gx-texto">Elige un modo.</div>}
        </div>
      </div>
    </div>
  );
}
